/**
 * Date utility functions for parsing and handling dates in the HomeGuardian app
 */

/**
 * Parses a date string in format "MM/DD, H:MM AM/PM" assuming current year
 * @param dateStr - Date string in format "MM/DD, H:MM AM/PM" (e.g. "08/13, 4:59 PM")
 * @returns A Date object with the current year, or null if parsing fails
 */
export const parseShortDateWithCurrentYear = (dateStr: string): Date | null => {
  try {
    const currentYear = new Date().getFullYear();
    const [datePart, timePart] = dateStr.split(', ');
    
    if (!datePart || !timePart) {
      return null;
    }
    
    const [month, day] = datePart.split('/').map(Number);
    
    // Parse time part (convert to 24-hour format if needed)
    const timeRegex = /(\d+):(\d+)\s+(AM|PM)/i;
    const timeMatch = timePart.match(timeRegex);
    
    if (!timeMatch) {
      return null;
    }
    
    const [, hours, minutes, period] = timeMatch;
    let hour = parseInt(hours, 10);
    
    // Convert to 24-hour format
    if (period.toUpperCase() === 'PM' && hour < 12) {
      hour += 12;
    } else if (period.toUpperCase() === 'AM' && hour === 12) {
      hour = 0;
    }
    
    // Create the Date object with the current year
    return new Date(currentYear, month - 1, day, hour, parseInt(minutes, 10));
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Determines if a date is stale (more than a specified number of hours ago)
 * @param dateStr - Date string to check
 * @param hoursThreshold - Number of hours after which data is considered stale
 * @returns boolean - true if the date is more than hoursThreshold hours ago
 */
export const isDateStale = (dateStr: string | undefined, hoursThreshold = 24): boolean => {
  if (!dateStr) return false;
  
  const parsedDate = parseShortDateWithCurrentYear(dateStr);
  if (!parsedDate) return false;
  
  const now = new Date();
  const timeDiff = now.getTime() - parsedDate.getTime();
  const hoursDiff = timeDiff / (1000 * 60 * 60);
  
  return hoursDiff > hoursThreshold;
};
